/**
 * Edit Tag Dialog
 *
 * Dialog for creating a new tag or editing an existing one.
 * Account admins can optionally set a workspace, making it a workspace-level tag.
 * Workspace admins automatically create workspace-level tags.
 */

import { useEffect, useState } from 'react';
import { Tag, Workspace } from '../../apis/api-client';
import { workspacesClient } from '../../apis/client-instances';
import WorkspaceAutocomplete from '../common/WorkspaceAutocomplete';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '../ui';

interface EditTagDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (tagData: { name: string; workspaceId?: number | null }) => Promise<void>;
  tag?: Tag | null;
  isAccountAdmin: boolean;
}

export default function EditTagDialog({ open, onClose, onSave, tag, isAccountAdmin }: EditTagDialogProps) {
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [error, setError] = useState('');

  const isEditMode = !!tag;

  // Pre-fill form in edit mode
  useEffect(() => {
    if (tag) {
      setName(tag.name || '');
      // Resolve the workspace by id so the autocomplete can show its name.
      if (tag.workspaceId) {
        workspacesClient
          .getWorkspaceById(tag.workspaceId)
          .then((w) => setWorkspace(w ?? null))
          .catch(() => setWorkspace({ id: tag.workspaceId } as Workspace));
      } else {
        setWorkspace(null);
      }
    } else {
      setName('');
      setWorkspace(null);
    }
    setError('');
  }, [tag, open]);

  const handleNameChange = (value: string) => {
    setName(value);
    setError('');
  };

  const validate = () => {
    if (!name?.trim()) {
      setError('Tag name is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setLoading(true);
      await onSave({ name, workspaceId: workspace?.id ?? null });
      handleClose();
    } catch (error: any) {
      console.error('Failed to save tag:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setWorkspace(null);
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditMode ? 'Edit Tag' : 'Add Tag'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Tag Name"
            fullWidth
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            error={!!error}
            helperText={error}
            autoFocus
          />
          {isAccountAdmin && (
            <>
              <WorkspaceAutocomplete
                value={workspace}
                onChange={setWorkspace}
                disabled={!!tag?.id}
                size="medium"
                fullWidth
                label="Workspace (Optional)"
                placeholder="All Workspaces"
                helperText="Leave blank for all workspaces"
              />
              {!isEditMode && (
                <Alert severity="info">
                  {workspace
                    ? 'This tag will only be available to users in the selected workspace.'
                    : 'Account-wide tags are visible to all workspaces. Workspace admins can see them but cannot edit or delete them.'}
                </Alert>
              )}
            </>
          )}
          {!isAccountAdmin && !isEditMode && (
            <Alert severity="info" sx={{ mt: 0 }}>
              This tag will be specific to your workspace only.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={loading}>
          {loading ? <CircularProgress size={20} color="inherit" /> : isEditMode ? 'Save Changes' : 'Create Tag'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
